/**
 * SignalDrawer — 360px disposable drawer for a triggering signal (S3.3).
 *
 * Full rewrite per design ref
 * `design_handoff_echelon_2026_04_26/workspace/globeTakeover.jsx:250-362`
 * + PRD FR-6.1/FR-6.2 + sprint-plan AC-3.3.a/b. Takes `WorldMonitorSignal`
 * shapes (was `SignalRow` pre-S3.3; cluster derivation now proximity-based
 * per FR-6.1 √(Δlat²+Δlon²)<20 instead of `correlated_signals` lookup).
 *
 * Parent (GlobeTakeover via `children`) owns:
 *   - selection state (drives drawer mount)
 *   - cluster derivation (call `deriveProximityCluster` against live corpus)
 *   - investigate handler (S3.4 wires `useCreateInvestigation`)
 */

import { cn } from '@/lib/utils';
import type { WorldMonitorSignal } from '../../api/worldMonitor';
import { angularDistance } from './GlobeCanvas';

export interface SignalDrawerProps {
  signal: WorldMonitorSignal;
  /** Proximity siblings (excludes the triggering signal). Up to 3 nearest. */
  cluster?: readonly WorldMonitorSignal[];
  onClose: () => void;
  /** "Investigate" CTA. Receives the triggering signal + cluster for payload assembly. */
  onInvestigate?: (signal: WorldMonitorSignal, cluster: readonly WorldMonitorSignal[]) => void;
  /** True while the parent's create-investigation mutation is in flight (S3.4). */
  investigateInFlight?: boolean;
  /** Inline status/error rendered under the CTA. Used by S3.4 for the 401 JWT
   * affordance (SDD §7.2). null/undefined hides the slot. */
  actionError?: string | null;
  className?: string;
}

/** AC-3.4.c verbatim affordance for 401 responses. Exported for the wrapper
 * that drives `useCreateInvestigation` so the text lives in one place. */
export const JWT_AFFORDANCE_TEXT =
  'Operator action — JWT required. Mint via scripts/mint-operator-token.py and paste into localStorage.access_token.';

const SEV_COLOR: Record<string, string> = {
  critical: 'var(--sig-rose)',
  high: 'var(--sig-ember)',
  moderate: 'var(--sig-amber)',
  low: 'var(--sig-mint)',
};

const DOMAIN_COLOR: Record<string, string> = {
  geopolitical: 'var(--sig-iris)',
  finance: 'var(--sig-ember)',
  corporate: 'var(--sig-ember)',
  legal: 'var(--sig-ember)',
  academic: 'var(--sig-ember)',
  maritime: 'var(--sig-azure)',
  aviation: 'var(--sig-azure)',
  seismic: 'var(--sig-amber)',
  environmental: 'var(--sig-mint)',
  health: 'var(--sig-mint)',
  cyber: 'var(--sig-rose)',
  nuclear: 'var(--sig-amber)',
  sports: 'var(--sig-mint)',
  other: 'var(--e-text-muted)',
};

function formatTimeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function SignalDrawer({
  signal,
  cluster = [],
  onClose,
  onInvestigate,
  investigateInFlight = false,
  actionError,
  className,
}: SignalDrawerProps) {
  const sevColor = SEV_COLOR[signal.severity] ?? 'var(--sig-mint)';
  const domainColor = DOMAIN_COLOR[signal.domain] ?? 'var(--e-text-muted)';
  const ctaLabel = cluster.length >= 1 ? 'Investigate this cluster →' : 'Investigate signal →';

  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-label={`Signal ${signal.id}`}
      data-testid="signal-drawer"
      className={cn(
        'absolute right-4 top-4 bottom-4 w-[360px] flex flex-col z-[25]',
        'bg-[var(--e-bg-card)] border border-[var(--e-border-secondary)] rounded-md shadow-lg fade-in',
        className,
      )}
      style={{ borderLeft: `2px solid ${sevColor}` }}
    >
      {/* Header */}
      <div className="px-3.5 py-3 border-b border-[var(--e-border-secondary)]/60">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                backgroundColor: sevColor,
                ...(signal.severity === 'critical' ? { animation: 'pulse 1.4s ease-in-out infinite' } : {}),
              }}
            />
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]">
              SIGNAL · {signal.id}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close signal drawer"
            className="text-[14px] text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)] w-6 h-6 flex items-center justify-center"
          >
            ×
          </button>
        </div>
        <div className="mt-2 text-[13px] leading-snug text-[var(--e-text-primary)]">
          {signal.title}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span
            className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border"
            style={{ color: domainColor, borderColor: domainColor }}
          >
            {signal.domain}
          </span>
          <span
            className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border"
            style={{ color: sevColor, borderColor: sevColor }}
          >
            {signal.severity}
          </span>
          <span className="text-[9px] font-mono text-[var(--e-text-muted)] ml-auto">
            {signal.region} · {formatTimeAgo(signal.timestamp)}
          </span>
        </div>
      </div>

      {/* Metadata */}
      <div className="px-3.5 py-2.5 border-b border-[var(--e-border-secondary)]/60">
        <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] mb-1">
          SOURCE · CONFIDENCE
        </div>
        <div className="text-[12px] text-[var(--e-text-primary)] flex items-center gap-2 flex-wrap">
          <span className="font-mono">{signal.source}</span>
          <span className="text-[var(--e-text-muted)]">·</span>
          <span className="font-mono tabular-nums">{Math.round(signal.confidence * 100)}%</span>
        </div>
        <div className="mt-1.5 font-mono tabular-nums text-[10.5px] text-[var(--e-text-muted)]">
          {signal.geo.lat.toFixed(2)}°, {signal.geo.lon.toFixed(2)}°
        </div>
      </div>

      {/* Cluster */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-3.5 py-2.5">
          <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] mb-2">
            CLUSTER · {cluster.length} SIBLING{cluster.length === 1 ? '' : 'S'}
          </div>
          {cluster.length === 0 ? (
            <div
              className="text-[11px] text-[var(--e-text-muted)] italic px-2.5 py-2 rounded"
              style={{ background: 'var(--e-bg-inset, var(--e-bg-hover))' }}
              data-testid="signal-drawer-no-cluster"
            >
              No sibling signals detected in the synthesized 2000km window.
            </div>
          ) : (
            <ul className="list-none m-0 p-0 flex flex-col gap-1.5">
              {cluster.map((c) => {
                const cDomainColor = DOMAIN_COLOR[c.domain] ?? 'var(--e-text-muted)';
                return (
                  <li key={c.id} data-testid={`signal-drawer-cluster-row-${c.id}`}>
                    <div
                      className="px-2.5 py-1.5 rounded text-[11.5px] text-[var(--e-text-primary)] border border-[var(--e-border-secondary)]/60"
                      style={{ borderLeft: `2px solid ${cDomainColor}` }}
                    >
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="font-mono text-[var(--e-text-muted)]">{c.id}</span>
                        <span className="font-mono tabular-nums text-[var(--e-text-muted)]">
                          {formatTimeAgo(c.timestamp)}
                        </span>
                      </div>
                      <div className="text-[12px] mt-0.5">{c.title}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[var(--e-border-secondary)]/60 px-3.5 py-2.5 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded border border-[var(--e-border-secondary)] text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)]"
          >
            Dismiss
          </button>
          {onInvestigate && (
            <button
              type="button"
              onClick={() => onInvestigate(signal, cluster)}
              disabled={investigateInFlight}
              data-testid="signal-drawer-investigate-cta"
              className="flex-1 text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 rounded border border-[var(--sig-iris)] bg-[var(--sig-iris)] text-[var(--e-bg-deep,#000)] font-medium disabled:opacity-60 disabled:cursor-wait"
            >
              {investigateInFlight ? 'Creating…' : ctaLabel}
            </button>
          )}
        </div>
        {actionError && (
          <div
            role="status"
            data-testid="signal-drawer-action-error"
            className="text-[10.5px] font-mono leading-snug px-2.5 py-1.5 rounded border border-[var(--e-border-secondary)] text-[var(--sig-amber)]"
            style={{ borderLeft: '2px solid var(--sig-amber)' }}
          >
            {actionError}
          </div>
        )}
      </div>
    </aside>
  );
}

// ── FR-6.3 payload builder (pure helper, exported for vitest) ───────

/**
 * Assemble the `/api/v1/investigations/` POST body for a cluster CTA fire
 * per sprint-plan §S3.4 + FR-6.3. Shape locked:
 *
 *   inquiry_class: 'PATTERN_VERIFICATION'
 *   source_signal_ids: [signal.id, ...cluster.map(c => c.id)]
 *   domain_filters: [signal.domain]
 *   geo: { scope: 'regional', center: [lon, lat], radius_m: 100_000, derivation: 'evidence' }
 *
 * Note: `geo.center` is [longitude, latitude] (GeoJSON order), not [lat, lon].
 */
export interface CreateInvestigationPayload {
  inquiry_class: 'PATTERN_VERIFICATION';
  source_signal_ids: string[];
  domain_filters: string[];
  geo: {
    scope: 'regional';
    center: [number, number];
    radius_m: number;
    derivation: 'evidence';
  };
}

export function buildCreateInvestigationPayload(
  signal: WorldMonitorSignal,
  cluster: readonly WorldMonitorSignal[] = [],
): CreateInvestigationPayload {
  return {
    inquiry_class: 'PATTERN_VERIFICATION',
    source_signal_ids: [signal.id, ...cluster.map((c) => c.id)],
    domain_filters: [signal.domain],
    geo: {
      scope: 'regional',
      center: [signal.geo.lon, signal.geo.lat],
      radius_m: 100_000,
      derivation: 'evidence',
    },
  };
}

// ── Cluster derivation (pure helper, exported for vitest) ───────────

/**
 * Build the proximity cluster for `triggering` against the live corpus per
 * FR-6: great-circle central angle `angularDistance(...) < threshold°` (default
 * 20°), returning the N nearest signals excluding the triggering signal itself.
 * (cycle-109.1 FR-6: migrated off the old pole/antimeridian-broken Euclidean
 * `clusterDistance` to the shared haversine metric.)
 *
 * NOT H3. NOT backend-derived. Pure frontend math against geo coords.
 */
export function deriveProximityCluster(
  triggering: WorldMonitorSignal,
  corpus: readonly WorldMonitorSignal[],
  options: { threshold?: number; max?: number } = {},
): WorldMonitorSignal[] {
  const threshold = options.threshold ?? 20;
  const max = options.max ?? 3;

  return corpus
    .filter((s) => s.id !== triggering.id)
    .map((s) => ({ signal: s, distance: angularDistance(triggering.geo.lat, triggering.geo.lon, s.geo.lat, s.geo.lon) }))
    .filter((entry) => entry.distance < threshold)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, max)
    .map((entry) => entry.signal);
}
